import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, cents } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PickupPicker from "../components/PickupPicker";
import usePageMeta from "../hooks/usePageMeta";
import { track } from "../lib/analytics";
import { MAX_BOTTLES, priceFor, functionalBreakdown } from "../lib/pricing";

export default function Subscriptions() {
  usePageMeta("Weekly Juice Subscription with Dallas Pickup | Tiff Wellness Co.", "Set a recurring juice pickup every 7 or 14 days — any bottle count, automatic pack pricing, skip or cancel online anytime. Dallas gym pickup locations.", {
    jsonLd: { "@context": "https://schema.org", "@type": "Service", name: "Recurring juice pickup subscription", serviceType: "Subscription with gym pickup", provider: { "@type": "Organization", name: "tiff. wellness co." }, areaServed: "Dallas, TX" },
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [bottles, setBottles] = useState(3);
  const [cadence, setCadence] = useState("weekly");
  const [sel, setSel] = useState({});
  const [pickup, setPickup] = useState(null);
  const [preview, setPreview] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    const pre = localStorage.getItem("tw_sub_prefill");
    if (pre) {
      try {
        const { bottles: b, sel: s, cadence: c } = JSON.parse(pre);
        if (b >= 1 && b <= MAX_BOTTLES) { setBottles(b); setSel(s); }
        if (c === "weekly" || c === "biweekly") setCadence(c);
      } catch {}
      localStorage.removeItem("tw_sub_prefill");
    }
  }, []);

  const planKey = `sub_${bottles}_${cadence}`;
  const intervalDays = cadence === "weekly" ? 7 : 14;
  const total = useMemo(() => Object.values(sel).reduce((a, b) => a + b, 0), [sel]);
  const nfSel = useMemo(() => products.length ? Object.entries(sel).reduce((a, [s, q]) => a + ((products.find((p) => p.slug === s)?.category === "traditional") ? 0 : q), 0) : 0, [sel, products]);
  const selComplete = total === bottles;
  const planCents = selComplete ? priceFor(nfSel, total - nfSel) : priceFor(bottles, 0);
  const remaining = bottles - total;

  useEffect(() => {
    setPreview(null);
    if (!pickup || remaining !== 0) return;
    api.post("/checkout/subscription/preview", {
      plan_key: planKey, items: Object.entries(sel).map(([slug, qty]) => ({ slug, qty })),
      location_id: pickup.location_id, window_id: pickup.window_id,
      first_pickup_date: pickup.date, origin_url: window.location.origin,
    }).then((r) => setPreview(r.data)).catch(() => {});
  }, [planKey, pickup, remaining]); // eslint-disable-line

  const subscribe = async () => {
    if (!user || user === false) {
      localStorage.setItem("tw_sub_prefill", JSON.stringify({ bottles, sel, cadence }));
      navigate("/auth?next=/subscriptions");
      return;
    }
    setBusy(true);
    track("subscription_started", { plan_key: planKey, location_id: pickup.location_id });
    try {
      const { data } = await api.post("/checkout/subscription", {
        plan_key: planKey, items: Object.entries(sel).map(([slug, qty]) => ({ slug, qty })),
        location_id: pickup.location_id, window_id: pickup.window_id,
        first_pickup_date: pickup.date, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <p className="eyebrow mb-2">Subscriptions · proposed v1, test mode</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-3">Juice Subscriptions That Fit Your Dallas Routine</h1>
      <p className="text-sm text-[#171715]/70 max-w-2xl mb-8">Choose any number of bottles per pickup (up to {MAX_BOTTLES}), billed per cycle. Functional juices are $12 single / $28 per 3 / $54 per 6, applied automatically; Renew+ (traditional) is $10 per bottle. Weekly means every 7 days; biweekly means every 14 days. One pickup per paid cycle. No minimum commitment, no invented discounts.</p>

      <section className="mb-8">
        <h2 className="text-lg font-medium mb-3">1 · Choose your plan</h2>
        <div className="card-paper p-5 max-w-xl space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm font-medium">Bottles per pickup</span>
            <div className="flex items-center border border-[#d6cfc2] bg-white">
              <button data-testid="sub-bottles-dec" aria-label="Fewer bottles" className="min-h-[44px] min-w-[44px]" onClick={() => { setBottles(Math.max(1, bottles - 1)); }}>−</button>
              <span data-testid="sub-bottles-value" className="w-10 text-center font-mono">{bottles}</span>
              <button data-testid="sub-bottles-inc" aria-label="More bottles" className="min-h-[44px] min-w-[44px]" onClick={() => { setBottles(Math.min(MAX_BOTTLES, bottles + 1)); }}>+</button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm font-medium">How often</span>
            <div className="flex gap-2">
              <button data-testid="subscription-cadence-weekly" onClick={() => setCadence("weekly")}
                className={`min-h-[44px] px-4 border text-sm ${cadence === "weekly" ? "bg-[#171715] text-[#FBF6F1] border-[#171715]" : "bg-white border-[#EBD9D0]"}`}>Every 7 days</button>
              <button data-testid="subscription-cadence-biweekly" onClick={() => setCadence("biweekly")}
                className={`min-h-[44px] px-4 border text-sm ${cadence === "biweekly" ? "bg-[#171715] text-[#FBF6F1] border-[#171715]" : "bg-white border-[#EBD9D0]"}`}>Every 14 days</button>
            </div>
          </div>
          <div className="border-t border-[#EBD9D0] pt-3 text-sm flex justify-between">
            <span>{bottles} bottle{bottles > 1 ? "s" : ""} · every {intervalDays} days</span>
            <span data-testid="subscription-price" className="font-mono font-semibold">{cents(planCents)} per pickup{selComplete ? "" : " (all-functional estimate)"}</span>
          </div>
          <p className="text-xs text-[#171715]/55">
            {selComplete && nfSel > 0 ? `Functional (${nfSel}): ${functionalBreakdown(nfSel)} pricing applied automatically. ` : "Functional juices use automatic 3-pack / 6-pack combination pricing. "}
            Renew+ (traditional, no added supplement) is $10 per bottle — traditional pack pricing pending confirmation.
            Your exact per-pickup price is confirmed in the review step.
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-lg font-medium mb-3">2 · Choose your {bottles} bottle{bottles > 1 ? "s" : ""} <span className="font-mono text-sm text-[#171715]/60">({remaining > 0 ? `${remaining} left` : remaining < 0 ? `remove ${-remaining}` : "done"})</span></h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {products.map((p) => (
            <div key={p.slug} className="card-paper p-3 flex items-center gap-3">
              <span className="photo-panel p-1 shrink-0 flex items-center justify-center"><img src={p.image} alt="" width="26" height="56" className="h-14 w-auto object-contain" /></span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{p.name}</p>
                {p.functional_addition && <p className="text-[10px] font-mono uppercase text-[#A35847]">+ {p.functional_addition}</p>}
              </div>
              <div className="flex items-center gap-1">
                {sel[p.slug] > 0 && <button data-testid={`sub-remove-${p.slug}`} aria-label={`remove ${p.name}`} className="min-h-[44px] min-w-[44px] border border-[#EBD9D0]" onClick={() => setSel((s) => { const n = { ...s }; n[p.slug] -= 1; if (n[p.slug] <= 0) delete n[p.slug]; return n; })}>−</button>}
                {sel[p.slug] > 0 && <span className="font-mono text-sm w-5 text-center">{sel[p.slug]}</span>}
                <button data-testid={`sub-add-${p.slug}`} aria-label={`add ${p.name}`} disabled={remaining <= 0 || !p.available} className="min-h-[44px] min-w-[44px] bg-[#171715] text-[#FBF6F1] disabled:opacity-30"
                  onClick={() => setSel((s) => ({ ...s, [p.slug]: (s[p.slug] || 0) + 1 }))}>+</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-lg font-medium mb-3">3 · Choose your location &amp; first pickup</h2>
        <PickupPicker value={pickup} onChange={setPickup} />
      </section>

      <section className="card-paper p-5 max-w-xl">
        <h2 className="text-lg font-medium mb-3">4 · Review before consent</h2>
        {preview ? (
          <dl className="text-sm space-y-1.5" data-testid="subscription-preview">
            <div className="flex justify-between"><dt>First charge</dt><dd className="font-mono">{cents(preview.first_charge_cents)} today at checkout</dd></div>
            <div className="flex justify-between"><dt>First pickup</dt><dd>{preview.first_pickup} · {preview.first_pickup_window}</dd></div>
            <div className="flex justify-between"><dt>Next charge</dt><dd className="font-mono">{cents(preview.next_charge_cents)} on {new Date(preview.next_charge_at).toLocaleDateString("en-US", { timeZone: "America/Chicago" })}</dd></div>
            <div className="flex justify-between"><dt>Second pickup</dt><dd>{preview.second_pickup}</dd></div>
            <div className="flex justify-between"><dt>Recurrence</dt><dd>{preview.recurrence}</dd></div>
            <div className="flex justify-between"><dt>Selection lock</dt><dd>{preview.selection_cutoff_hours ? `${preview.selection_cutoff_hours}h before each pickup` : "at least 24 hours before each pickup window"}</dd></div>
          </dl>
        ) : <p className="text-sm text-[#171715]/60" data-testid="subscription-preview-pending">Complete plan, bottles and pickup selection to preview exact charges and dates.</p>}
        <label className="flex items-start gap-2 text-xs text-[#171715]/75 mt-4">
          <input data-testid="subscription-consent-checkbox" type="checkbox" className="mt-0.5 h-5 w-5" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={!preview} />
          <span>I authorize recurring charges of {cents(preview ? preview.first_charge_cents : planCents)} every {intervalDays} days until I skip, pause or cancel. Cancellation is immediate and available on my account page.</span>
        </label>
        <button data-testid="subscription-checkout-button" className="btn-ink w-full mt-4" disabled={!preview || !consent || busy} onClick={subscribe}>
          {busy ? "Redirecting…" : user && user !== false ? "Start subscription (Stripe test)" : "Sign in to subscribe"}
        </button>
      </section>
    </div>
  );
}
